// Temporary data store - will be replaced with Sanity CMS

export interface ProjectSection {
  title: string;
  content: string;
}

export interface SolutionPoint {
  label: string;
  text: string;
}

/** The subset of a project's text fields that PortfolioSection (and
 * eventually the project detail page) renders, and that can be translated
 * per-locale via `Project.translations`. */
export interface LocalizedProjectFields {
  title: string;
  category: string;
  description: string;
}

export interface Project {
  id: number | string;
  title: string;
  slug: string;
  category: string;
  year: string;
  description: string;
  image: string;
  heroImage?: string;
  fullWidthImage?: string;
  video?: string;
  technologies: string[];
  liveUrl?: string;
  sections?: ProjectSection[];
  layoutVariant?: string;
  /** Dilacode-style "The Website" / "The Challenge" content */
  challengeTitle?: string;
  challengeContent?: string;
  /** Dilacode-style "The Solution" content */
  solutionIntro?: string;
  solutionPoints?: SolutionPoint[];
  /** Quote overlaid on the full-width image */
  heroQuote?: string;
  /** Per-locale overrides for the fields PortfolioSection displays. Any
   * field left out falls back to the English value (see `localizeProject`
   * in `src/lib/i18n`). Content is added by a translator later; this is
   * just the structure. */
  translations?: {
    ar?: Partial<LocalizedProjectFields>;
    ckb?: Partial<LocalizedProjectFields>;
  };
  /** Additional content images shown in project detail */
  contentImages?: string[];
}

export const projects: Project[] = [
  {
    id: 'parezar',
    title: 'Parezar',
    slug: 'parezar-ai-legal-platform',
    category: 'AI Integration',
    year: '2026',
    description: 'An AI legal platform for Iraq. A multi-agent architecture answers legal questions, drafts contracts, and searches Iraqi laws and regulations. Built on Rust and Python so it stays fast under load — a working bridge between citizens and the Iraqi legal system.',
    translations: {
      ar: {
        category: 'دمج الذكاء الاصطناعي',
        description:
          'منصة قانونية بالذكاء الاصطناعي مخصصة للعراق. بنية متعددة الوكلاء تجاوب على الأسئلة القانونية، تصيغ العقود، وتبحث بالقوانين والأنظمة العراقية. مبنية على Rust وPython عشان تضل سريعة تحت الضغط: جسر شغال بين المواطن والنظام القانوني العراقي.',
      },
      ckb: {
        category: 'تێکەڵکردنی زیرەکی دەستکرد',
        description:
          'پلاتفۆرمێکی یاسایی بە زیرەکی دەستکرد بۆ عێراق. پێکهاتەیەکی فرە کارگێڕ وەڵامی پرسیاری یاسایی دەداتەوە، گرێبەست ئامادە دەکات، و بەدوای یاسا و ڕێنماییە عێراقییەکاندا دەگەڕێت. لەسەر Rust و Python بنیات نراوە بۆ ئەوەی خێرایی خۆی هەر بپارێزێت لەژێر گوشاردا: پردێکی کاریگەر لەنێوان هاوڵاتی و سیستەمی یاسایی عێراق.',
      },
    },
    image: '/images/projects/parezar/thumbnail.webp',
    heroImage: '/images/projects/parezar/hero.webp',
    fullWidthImage: '/images/projects/parezar/full.webp',
    technologies: ['Flutter', 'Next.js', 'Python', 'Rust', 'LLMs', 'Vector Search', 'Multi-Agent Systems'],
    liveUrl: 'https://parezar.org',
    layoutVariant: 'parezar',
    challengeTitle: 'The Platform',
    challengeContent: 'Parezar (meaning "Protector" in Kurdish) closes the access gap in the Iraqi legal system. We built a multi-agent system that reads legal context across Arabic, Kurdish, and English, not just keywords. It handles instant 24/7 legal Q&A and drafts legally compliant contracts in seconds.',
    solutionIntro: 'We built a multi-layered AI system tuned for speed, security, and accuracy:',
    solutionPoints: [
      { label: 'Multi-Agent Architecture', text: 'Specialized backend agents built in Python and Rust collaborate to process complex legal queries, ensuring high accuracy and verification against current Iraqi laws.' },
      { label: 'AI Contract Engine', text: 'Automated generation of professionally drafted legal documents and contracts, fully compliant with Iraqi commercial and personal status laws.' },
      { label: 'Smart Vector Search', text: 'A semantic search engine utilizing embeddings to find relevant laws, regulations, and precedents instantly, even with natural language queries.' },
      { label: 'Cross-Platform Ecosystem', text: 'The same account and data across a fast Flutter mobile app and a Next.js web portal that holds up under load.' },
    ],
    heroQuote: 'Bridging the gap between code and law in modern Iraq.',
    contentImages: ['/images/projects/parezar/architecture.webp'],
    sections: [
      {
        title: 'Overview',
        content: 'Parezar is an AI-powered legal platform built for Iraq. It gives citizens and legal professionals 24/7 access to legal assistance, smart search across Iraqi regulations, and automated tools for contract generation and case management.',
      },
      {
        title: 'The Challenge',
        content: 'The Iraqi legal system is complex and often difficult for the average citizen to navigate. Laws and regulations are spread across fragmented databases, and professional legal advice can be expensive and slow to access. Furthermore, the need for a bilingual (Arabic/Kurdish) solution that understands the specific nuances of regional law presented a significant technical hurdle.',
      },
      {
        title: 'Our Approach',
        content: 'We built a multi-agent architecture. Rust runs the security and data-processing layers where speed matters most, while Python handles the LLM orchestration and embedding logic. The frontend splits between a Flutter mobile app for quick access and a Next.js web dashboard for deep legal research and document management. The split lets the system scale and keeps each surface fast.',
      },
      {
        title: 'Key Features',
        content: '24/7 AI Legal Assistant with context-aware Q&A. Automated Contract Generator for Iraqi legal documents. Smart Legal Search engine using vector embeddings. A directory of verified Iraqi lawyers. Secure Case Management system with deadline reminders. Full bilingual support for Arabic and Kurdish users.',
      },
      {
        title: 'Impact',
        content: 'Parezar has democratized legal access in Iraq, allowing users to get instant answers to critical questions. The platform supports over 500+ expert lawyers and has helped resolve thousands of cases through its management tools. It stands as a testament to how advanced AI agents can be applied to solve real-world accessibility issues in emerging markets.',
      },
    ],
  },
  {
    id: 1,
    title: 'Trendz Mart',
    slug: 'trendz-app-development',
    category: 'Mobile App',
    year: '2024',
    description: 'An e-commerce mobile app built for the Iraqi market, with AI product recommendations and a checkout that gets out of the way. We handled the hard parts — regional payment methods and full Kurdish and Arabic support — so the app works for the people who actually shop here.',
    translations: {
      ar: {
        category: 'تطبيق موبايل',
        description:
          'تطبيق تسوق إلكتروني مبني للسوق العراقي، بتوصيات منتجات بالذكاء الاصطناعي وعملية شراء ما تعقّد وياك. تكفلنا بالأجزاء الصعبة: طرق الدفع المحلية ودعم كامل للكوردي والعربي، عشان التطبيق يشتغل فعلاً للناس اللي تتسوق هنا.',
      },
      ckb: {
        category: 'ئەپی مۆبایل',
        description:
          'ئەپێکی بازرگانی ئەلیکترۆنی بۆ بازاڕی عێراق دروستکراوە، بە پێشنیاری بەرهەم بە زیرەکی دەستکرد و پرۆسەیەکی کڕین کە ئاسانە. ئێمە بەشە قورسەکانمان چارەسەر کرد: شێوازی پارەدانی ناوچەیی و پشتگیری تەواو بۆ کوردی و عەرەبی، بۆ ئەوەی ئەپەکە بەڕاستی بۆ ئەو کەسانە کاربکات کە لێرە کڕین دەکەن.',
      },
    },
    image: '/images/projects/trendz-mart.webp',
    heroImage: '/images/projects/trendz-hero.webp',
    fullWidthImage: '/images/projects/trendz_rocks.webp',
    technologies: ['React Native', 'Node.js', 'MongoDB', 'AI/ML'],
    challengeTitle: 'The App',
    challengeContent: 'Trendz Mart changed how people shop online in the Kurdistan Region, giving users a fast, easy way to find products through AI recommendations. The app serves Kurdish and Arabic customers in their own language without forcing a restart to switch, and it has processed thousands of orders at a 94% delivery success rate.',
    solutionIntro: 'We built a mobile-first e-commerce platform designed for the unique challenges of the Iraqi market:',
    solutionPoints: [
      { label: 'AI Recommendations', text: 'Machine learning algorithms trained on regional purchasing patterns deliver highly relevant product suggestions, driving a 32% increase in average cart value.' },
      { label: 'Bilingual Interface', text: 'Switch between Kurdish and Arabic without a restart, with the full app translated in both.' },
      { label: 'Hybrid Payments', text: 'Cash-on-delivery alongside digital wallets, addressing limited card adoption in the region.' },
      { label: 'Smart Logistics', text: 'Custom address system using landmark-based navigation and real-time inventory sync across multiple vendor warehouses.' },
    ],
    heroQuote: 'Modern e-commerce, built for the Kurdistan Region.',
    contentImages: ['/images/projects/trendz_app_1x_05.webp', '/images/projects/trendz2_05.webp', '/images/projects/trendz_rocks_05.webp'],
    sections: [
      {
        title: 'Overview',
        content: 'Trendz Mart is a mobile-first e-commerce platform designed to bring modern online shopping to the Kurdistan Region of Iraq. The app delivers personalized product discovery through machine learning algorithms while maintaining the trust and familiarity that local shoppers expect from traditional retail.',
      },
      {
        title: 'The Challenge',
        content: 'The Iraqi e-commerce landscape presents unique obstacles: fragmented payment systems with limited card adoption, unreliable address infrastructure for delivery logistics, and a bilingual user base that expects native-quality experiences in both Kurdish and Arabic. Existing solutions were either too Western-centric or lacked the polish to build consumer trust.',
      },
      {
        title: 'Our Approach',
        content: 'We built the application on React Native for cross-platform reach, with a Node.js backend designed for the realities of variable network conditions. The AI recommendation engine was trained on regional purchasing patterns rather than Western consumer data, resulting in significantly more relevant product suggestions. We integrated cash-on-delivery alongside digital payment options and built a custom address system using landmark-based navigation.',
      },
      {
        title: 'Key Features',
        content: 'AI-powered product recommendations trained on local purchasing behavior. Bilingual interface that switches between Kurdish and Arabic without a restart. Hybrid payment system supporting cash-on-delivery and digital wallets. Real-time inventory sync across multiple vendor warehouses. Push notification campaigns with 3x higher engagement than industry average.',
      },
      {
        title: 'Impact',
        content: 'Within three months of launch, Trendz Mart processed over 5,000 orders with a 94% delivery success rate. The AI recommendation engine drove a 32% increase in average cart value compared to manual browsing. The platform now serves as a model for how e-commerce can be localized for emerging markets in the MENA region.',
      },
    ],
  },
  {
    id: 2,
    title: 'Zubene',
    slug: 'zubene',
    category: 'Mobile App',
    year: '2024',
    description: 'A social platform that connects people through shared interests and local events across Iraqi cities. Zubene turns online connections into real-world meetups — users organize, discover, and join everything from cultural gatherings to professional events.',
    translations: {
      ar: {
        category: 'تطبيق موبايل',
        description:
          'منصة اجتماعية تربط الناس عن طريق الاهتمامات المشتركة والفعاليات المحلية بمدن العراق. Zubene يحوّل التواصل الأونلاين للقاءات حقيقية: المستخدمين ينظمون، يكتشفون، وينضمون لكل شي من التجمعات الثقافية لين الفعاليات المهنية.',
      },
      ckb: {
        category: 'ئەپی مۆبایل',
        description:
          'پلاتفۆرمێکی کۆمەڵایەتییە کە خەڵک لە ڕێگەی بەرژەوەندی هاوبەش و ڕووداوی ناوخۆییەوە لە شارەکانی عێراق پەیوەست دەکاتەوە. Zubene پەیوەندی ئۆنلاین دەگۆڕێت بۆ کۆبوونەوەی ڕاستەقینە: بەکارهێنەران ڕێکدەخەن، دەدۆزنەوە و بەشداری دەکەن لە هەموو شتێک، لە کۆبوونەوەی کولتووری تا ڕووداوی پیشەیی.',
      },
    },
    image: '/images/projects/zubene.webp',
    heroImage: '/images/projects/zubene-hero.webp',
    fullWidthImage: '/images/projects/zubene_rocks.webp',
    technologies: ['Flutter', 'Firebase', 'Google Maps API'],
    challengeTitle: 'The Challenge',
    challengeContent: 'Iraq has a vibrant social culture, but digital tools for community organizing are fragmented across WhatsApp groups, Instagram pages, and word-of-mouth. Zubene needed a single platform where people could discover local events, form interest-based groups, and coordinate meetups — while respecting the privacy expectations and cultural norms of the region.',
    solutionIntro: 'Zubene was built as a mobile-first social platform designed to move conversations offline and into real gatherings:',
    solutionPoints: [
      { label: 'Interest Groups', text: 'Moderated community spaces where people organize around shared interests instead of scrolling a public feed.' },
      { label: 'Hyperlocal Discovery', text: 'Google Maps-powered event discovery with venue categories built for how people actually gather in Iraqi cities.' },
      { label: 'Real-Time Messaging', text: 'Group chats and event updates sync instantly through Firebase, so coordination happens in the app instead of scattered chats.' },
      { label: 'Privacy Controls', text: 'Granular visibility settings built around regional privacy expectations, not a one-size-fits-all Western default.' },
    ],
    heroQuote: 'Turning online connections into real-world gatherings.',
    contentImages: ['/images/projects/zubene_rocks_1x.webp', '/images/projects/zubene2.webp'],
    sections: [
      {
        title: 'Overview',
        content: 'Zubene — meaning "community" in Kurdish — is a social platform purpose-built for connecting people in Iraqi cities through shared interests and local events. Unlike global social networks that optimize for endless scrolling, Zubene is designed to move conversations offline and into real-world gatherings.',
      },
      {
        title: 'The Challenge',
        content: 'Iraq has a vibrant social culture, but digital tools for community organizing are fragmented across WhatsApp groups, Instagram pages, and word-of-mouth. There was no unified platform where people could discover local events, form interest-based groups, and coordinate meetups — all while respecting the cultural norms and privacy expectations of the region.',
      },
      {
        title: 'Our Approach',
        content: 'We chose Flutter for rapid cross-platform development and Firebase for real-time data synchronization that keeps group chats and event updates instant. The Google Maps API integration provides hyperlocal event discovery with culturally relevant venue categorization. Privacy controls were designed with regional expectations in mind, offering granular visibility settings that go beyond typical Western social platforms.',
      },
      {
        title: 'Key Features',
        content: 'Interest-based group creation with moderated community spaces. Hyperlocal event discovery powered by Google Maps with custom venue categories. Real-time group messaging with media sharing and event coordination tools. Privacy-first architecture with granular visibility controls. Bilingual support with context-aware content moderation.',
      },
      {
        title: 'Impact',
        content: 'Zubene has facilitated over 200 community events in its first quarter, with user retention rates 40% above regional benchmarks for social apps. The platform has become particularly popular among young professionals and university students seeking meaningful local connections beyond traditional social media.',
      },
    ],
  },
  {
    id: 3,
    title: 'Swan Villages',
    slug: 'swan-villages',
    category: 'Web Design',
    year: '2024',
    description: 'A real estate website for one of Erbil\'s high-end residential developments, with property showcases and virtual tours that let buyers walk the homes on screen. Visual storytelling up front, and the hard numbers serious buyers come for one click away.',
    translations: {
      ar: {
        category: 'تصميم مواقع',
        description:
          'موقع عقاري لوحدة من أرقى المجمعات السكنية بأربيل، بعرض للعقارات وجولات افتراضية تخلي المشتري يتمشى بالبيت من على الشاشة. قصة بصرية بالواجهة، والأرقام المهمة اللي يدور عليها المشتري الجاد بضغطة وحدة.',
      },
      ckb: {
        category: 'دیزاینی وێب',
        description:
          'ماڵپەڕێکی خانووبەرە بۆ یەکێک لە باشترین پرۆژە نیشتەجێبوونەکانی هەولێر، بە پیشاندانی خانووەکان و گەشتی مەجازی کە کڕیار دەتوانێت خانووەکە لەسەر شاشە بگەڕێت. چیرۆکێکی بینراو لە پێشەوە، و ژمارە گرنگەکانیش کە کڕیاری جدی بەدوایدا دەگەڕێت، تەنها بە یەک کرتەوە دوور نییە.',
      },
    },
    image: '/images/projects/swan-villages.webp',
    heroImage: '/images/projects/swan-hero.webp',
    fullWidthImage: '/images/projects/swan.webp',
    technologies: ['Next.js', 'Three.js', 'Sanity CMS'],
    liveUrl: 'https://swanvillages.com',
    video: '/images/projects/swan_desktop.mp4',
    contentImages: ['/images/projects/mobile.webp'],
    challengeTitle: 'The Website',
    challengeContent: 'The Swan Village website has successfully enhanced the resort\'s online presence, providing guests with a user-friendly, luxurious platform to book their stays. Resort management praised the admin panel for its simplicity and efficiency in managing daily operations, allowing them to focus more on delivering excellent guest services.',
    solutionIntro: 'We delivered a feature-rich, visually stunning website that offers:',
    solutionPoints: [
      { label: 'Elegant User Interface', text: 'A luxurious design that reflects the Swan Village brand and provides guests with an easy-to-navigate booking system.' },
      { label: 'Booking System', text: 'Guests can effortlessly check room availability, choose their preferred stay, and make secure payments for their reservations.' },
      { label: 'Admin Panel', text: 'The custom admin panel gives hotel staff full control over room management, booking approvals, and real-time updates on guest check-ins.' },
      { label: 'Mobile Responsiveness', text: 'The site looks and works correctly across all screen sizes, so guests on phones get the same experience as on desktop.' },
    ],
    sections: [
      {
        title: 'Overview',
        content: 'Swan Villages is the digital flagship for a luxury residential compound in Erbil, Kurdistan Region. The website serves as both a marketing platform and a practical tool for prospective buyers, combining cinematic visual storytelling with detailed property specifications, virtual tours, and direct inquiry functionality.',
      },
      {
        title: 'The Challenge',
        content: 'Luxury real estate in the Kurdistan Region faces a unique marketing challenge: buyers are often international, making purchase decisions remotely, and expect the same caliber of digital experience they encounter from developments in Dubai or London. The existing web presence was a basic brochure site that failed to convey the premium positioning of the development.',
      },
      {
        title: 'Our Approach',
        content: 'We built the site on Next.js for performance and SEO, with Three.js powering interactive 3D property visualizations that let remote buyers explore floor plans spatially. Sanity CMS gives the Swan Villages marketing team full control over property listings, pricing, and promotional content without developer intervention. Every interaction was designed to feel as premium as the properties themselves.',
      },
      {
        title: 'Key Features',
        content: 'Interactive 3D property tours built with Three.js for spatial exploration. Dynamic property listings managed through Sanity CMS with real-time updates. Performance-optimized media delivery for high-resolution property photography. Integrated inquiry system with CRM connectivity for the sales team. Multilingual content supporting English, Kurdish, and Arabic audiences.',
      },
      {
        title: 'Impact',
        content: 'The redesigned website increased qualified inquiry submissions by 180% within the first two months. Average session duration tripled compared to the previous site, with the 3D tour feature showing a 65% engagement rate among visitors. The site has become a reference point for luxury real estate digital presence in the region.',
      },
    ],
  },
  {
    id: 4,
    title: 'Batumi View',
    slug: 'batumi-view',
    category: 'Mobile App',
    year: '2024',
    description: 'A guest companion app for Batumi View Hotel. Guests book rooms, order food and laundry, and reach support in real time — all in an interface that matches the hotel itself.',
    translations: {
      ar: {
        category: 'تطبيق موبايل',
        description:
          'تطبيق يرافق ضيوف فندق Batumi View. الضيوف يحجزون غرف، يطلبون أكل وغسيل، ويوصلون للدعم بلحظتها: كل شي بواجهة تناسب الفندق نفسه.',
      },
      ckb: {
        category: 'ئەپی مۆبایل',
        description:
          'ئەپێکی هاوڕێی میوان بۆ هۆتێلی Batumi View. میوانەکان ژوور مۆل دەکەن، خواردن و جلشۆردن داوا دەکەن، و بە کاتی ڕاستەقینە پەیوەندی بە پشتگیرییەوە دەکەن: هەمووی لە ڕووکارێکدا کە لەگەڵ هۆتێلەکەدا دەگونجێت.',
      },
    },
    image: '/images/projects/batumi-view.webp',
    heroImage: '/images/projects/batumi-view-hero.webp',
    fullWidthImage: '/images/projects/mobile.webp',
    technologies: ['Flutter', 'Firebase', 'Node.js'],
    challengeTitle: 'The Challenge',
    challengeContent: 'The client wanted one mobile app that covered every guest need in a simple, intuitive way. The job was to bring room booking, food and laundry ordering, and customer support into a single app without making it feel cluttered, while keeping the upscale feel of a luxury hotel. The app also needed an admin panel so staff could manage hotel operations, from room availability to guest services.',
    solutionIntro: 'Batumi View was developed as a feature-rich, user-centric mobile app that offers the following:',
    solutionPoints: [
      { label: 'Room Booking', text: 'Guests can browse available rooms and book their stay directly through the app.' },
      { label: 'Food & Laundry Orders', text: 'Guests can easily place orders for food and laundry services with just a few taps.' },
      { label: 'Real-Time Support', text: 'Integrated chat and call features allow guests to instantly contact hotel staff for any needs.' },
      { label: 'Admin Panel', text: 'A separate admin dashboard allows hotel staff to manage rooms, monitor guest requests, and handle operations efficiently.' },
    ],
    contentImages: ['/images/projects/batumi_food_ordering.webp', '/images/projects/batumi_mobile_home.webp', '/images/projects/batumi_web_dashboard.webp'],
    sections: [
      {
        title: 'Overview',
        content: 'Batumi View is a luxury hotel companion app built to improve every part of the guest experience at Batumi View Hotel. From room booking to food ordering and real-time support, the app puts every hotel service in one place.',
      },
      {
        title: 'The Challenge',
        content: 'The client wanted one mobile app that covered every guest need in a simple, intuitive way. The job was to bring room booking, food and laundry ordering, and customer support into a single app without making it feel cluttered, while keeping the upscale feel of a luxury hotel.',
      },
      {
        title: 'Our Approach',
        content: 'We built the app on Flutter for cross-platform deployment with Firebase powering real-time data sync. The design language was carefully crafted to reflect the hotel\'s premium brand identity while keeping interactions intuitive and accessible.',
      },
      {
        title: 'Key Features',
        content: 'Room booking with availability calendar. Food and laundry ordering with real-time status tracking. Integrated chat and call support for instant guest assistance. Admin dashboard for managing all hotel operations.',
      },
      {
        title: 'Impact',
        content: 'The app has streamlined hotel operations and enhanced the guest experience, with over 80% of guests using the app for room service orders within the first month of launch.',
      },
    ],
  },
  {
    id: 5,
    title: 'Smart Community Platform',
    slug: 'smart-community-platform',
    category: 'Mobile App',
    year: '2024',
    description: 'A resident super-app and admin platform for a gated development in the UAE. One app for payments, service requests, maintenance, security approvals, school and gym access, local commerce, solar monitoring, energy use, and utility notices — instead of ten.',
    image: '/images/projects/sse-hero.webp',
    heroImage: '/images/projects/sse-hero.webp',
    fullWidthImage: '/images/projects/sse-desktop.webp',
    technologies: ['Flutter', 'Node.js', 'Firebase', 'IoT', 'Payment Gateway'],
    challengeTitle: 'The Platform',
    challengeContent: 'The Smart Community Platform serves as the operating system of a gated residential development — one place for residents to pay fees, order services, report issues, approve visitors, access school and gym services, monitor solar and energy performance, and interact with the community. For the operator, it consolidates collections, digital operations, service workflows, transaction monetization, resident communication, and infrastructure tracking into a single dashboard.',
    solutionIntro: 'We engineered a dual-layer platform — a resident super-app and an admin operations system — designed to generate revenue while improving community life:',
    solutionPoints: [
      { label: 'Resident Super App', text: 'A single interface for payments, billing, service requests, visitor approvals, school and gym access, marketplace ordering, solar monitoring, and community announcements.' },
      { label: 'Admin Operations', text: 'Billing oversight, collections reconciliation, ticket management with SLAs, vendor management, analytics, and energy infrastructure dashboards.' },
      { label: 'Energy & Solar Layer', text: 'Real-time rooftop solar production monitoring, household consumption views, grid vs. solar comparison, battery status, and carbon impact reporting.' },
      { label: 'Revenue Engine', text: 'Transaction processing fees, marketplace commissions, booking fees, gym margins, and school payment processing — turning the platform into a recurring revenue asset.' },
    ],
    heroQuote: 'The operating system of a smart community.',
    contentImages: ['/images/projects/sse-payments.webp', '/images/projects/sse-solar.webp', '/images/projects/sse-profile.webp'],
    sections: [
      {
        title: 'Overview',
        content: 'The Smart Community Platform is a single digital system built for a gated residential development in the UAE. It combines a resident-facing super-app with an admin operations platform, covering payments, service workflows, maintenance, security, lifestyle services, and energy management in one place.',
      },
      {
        title: 'The Challenge',
        content: 'Leading communities in the MENA region — The Sustainable City developments, ROSHN, and Aldar — are moving toward integrated, sustainability-led, digitally managed living. The client needed a platform that would position their development alongside these benchmarks: not just a resident convenience app, but a revenue-generating operational layer that improves fee collection, opens digital income streams, and strengthens the project\'s premium market positioning.',
      },
      {
        title: 'Our Approach',
        content: 'We designed a phased rollout across three stages: core launch (payments, ticketing, visitor approvals), commerce and lifestyle (marketplace, gym, school, facility booking), and energy and infrastructure (solar dashboards, battery monitoring, outage communications). The architecture supports IoT integrations for smart meters and inverters, local payment gateways, and real-time analytics across all community systems.',
      },
      {
        title: 'Key Features',
        content: 'Digital wallet with Visa, Mastercard, and local gateway support. Issue reporting with photo attachments and SLA tracking. Security gate approvals with visitor, delivery, and contractor workflows. School fee payments, pickup authorization, and transport alerts. Gym memberships, class bookings, and facility reservations. Marketplace with grocery, pharmacy, laundry, and home services. Rooftop solar production monitoring with savings and carbon reporting. Generator and plant status with outage alerts.',
      },
      {
        title: 'Impact',
        content: 'The platform is projected to generate over $38,000 annually in platform fees alone from a 200-unit community — through transaction processing, marketplace commissions, booking fees, and subscription margins. Beyond direct revenue, the platform improves collection consistency, reduces administrative overhead, and strengthens the development\'s valuation narrative as a smart, sustainability-led community.',
      },
    ],
  },
  {
    id: 6,
    title: 'Beyond',
    slug: 'beyond-logistics',
    category: 'Cross-border Commerce',
    year: '2024',
    description: 'A cross-border shopping platform that gives customers access to premium UK retailers — Selfridges, Harrods, John Lewis, Next, Debenhams, Primark — through a request-then-quote ordering flow, paired with an AI retailer co-pilot that surfaces conversion-driving promotional actions for one-click human approval.',
    image: '/images/projects/beyond/thumbnail.webp',
    heroImage: '/images/projects/beyond/hero.webp',
    fullWidthImage: '/images/projects/beyond/full.webp',
    technologies: ['Flutter', 'Microservices', 'Redis', 'RabbitMQ', 'AI/ML', 'Explainable AI', 'Global Logistics APIs'],
    challengeTitle: 'The Platform',
    challengeContent: 'Premium UK retailers — Selfridges, Harrods, John Lewis, Next, Debenhams, Primark — don\'t ship internationally to most of the markets where their products are in demand. Beyond closes that gap. Customers browse a curated catalogue of UK brands inside a single mobile app, assemble an order across multiple retailers, and submit a request. The operations team sources each item, returns a confirmed quote (with shipping and service fees) through the app, and the customer accepts or declines before any payment is taken. Every parcel is tracked through eight discrete stages from request to door.',
    solutionIntro: 'We engineered Beyond as two coordinated surfaces — a consumer shopping app and an operator dashboard with an AI retailer co-pilot — sharing a single event-driven backend:',
    solutionPoints: [
      { label: 'Curated UK Retailer Catalogue', text: 'Customers browse premium UK brands — Selfridges, Harrods, John Lewis, Next, Debenhams, Primark — in a single branded grid, with country-of-origin selection and per-retailer product detail pages.' },
      { label: 'Request, Quote, Confirm Flow', text: 'An order isn\'t a checkout — it\'s a request. Customers submit, the operations team sources and prices each item, and a confirmed quote (with shipping and service fees) returns through the app. The customer accepts or declines before any payment. Every order carries a traceable GRO-XXXXXXX-XXXX reference across eight fulfillment stages.' },
      { label: 'AI Retailer Co-pilot with Explainability', text: 'The operator dashboard surfaces AI-generated promotional suggestions — discount offers, free-shipping highlights, messaging nudges — each tagged with a confidence score and a full explainability panel showing the top contributing signals (dwell time, hover hesitation, cart value, first-time-buyer status). Operators approve or reject with one click, governed by a configurable auto-approve threshold.' },
      { label: 'Guardrails, Simulations & Tunable Triggers', text: 'A safety-controls layer constrains what the AI can act on, a simulation engine lets operators preview suggestions before they go live, and trigger weights are configurable inside the dashboard — so commercial strategy shapes AI behaviour without code changes.' },
    ],
    heroQuote: 'Britain\'s best, brought to you.',
    contentImages: [
      '/images/projects/beyond/retailers.webp',
      '/images/projects/beyond/add-to-order.webp',
      '/images/projects/beyond/order-submitted.webp',
      '/images/projects/beyond/dashboard-offers.webp',
    ],
    sections: [
      {
        title: 'Overview',
        content: 'Beyond connects shoppers in markets without direct UK retailer shipping to brands like Selfridges, Harrods, John Lewis, Next, Debenhams, and Primark — through a curated mobile catalogue and an operations team that handles every parcel from sourcing to doorstep delivery.',
      },
      {
        title: 'The Shopping Experience',
        content: 'Customers tap a retailer brand, browse its products, add an item to their order with size and variant selections, and assemble a multi-retailer cart in a single flow. The cross-border-shipping context is built into every screen — country of origin, retailer attribution per item, and live cart counts across multiple shops at once.',
      },
      {
        title: 'The Order Lifecycle',
        content: 'An order isn\'t a checkout — it\'s a request. Submitting one generates a unique reference (GRO-XXXXXXX-XXXX), notifies the operations team, and waits for a quote. The customer is alerted when pricing is set, accepts or declines through the app, and pays only on acceptance. From there, the parcel is tracked through eight discrete stages from sourcing to delivery.',
      },
      {
        title: 'The AI Retailer Co-pilot',
        content: 'The operator dashboard pairs human operators with an explainable AI engine. Each promotional suggestion — discount offers, free-shipping highlights, messaging nudges — comes tagged with a confidence score and a contributing-factors panel showing exactly which signals (dwell time, hover hesitation, cart value, first-time-buyer status) drove the recommendation. A configurable auto-approve threshold lets high-confidence actions execute autonomously while everything below it routes to the human queue. Trigger weights and offer types are tunable inside the dashboard — so commercial strategy shapes AI behaviour without code changes.',
      },
    ],
  },
  {
    id: 7,
    title: 'Hotel Management',
    slug: 'hotel-management-erp',
    category: 'Hospitality ERP',
    year: '2024',
    description: 'An ERP, booking engine, and property management system for large hotel chains, in one connected platform.',
    image: '/images/projects/hotel-management/thumbnail.webp',
    technologies: ['Flutter', 'Node.js', 'PostgreSQL', 'Booking Engine', 'PMS'],
    layoutVariant: 'zubene',
    challengeTitle: 'The Platform',
    challengeContent: 'A hotel chain operating multiple properties needed a single system to replace a stack of disconnected tools — front-desk PMS, booking engine, finance, housekeeping, and reporting — without a steep retraining curve for staff. The goal was one operational layer that worked the same on a tablet at the front desk as it did on a back-office laptop.',
    solutionIntro: 'We built a unified hospitality platform with three coordinated surfaces:',
    solutionPoints: [
      { label: 'Property Management System', text: 'Reservations, room status, check-in/out flows, guest profiles, and housekeeping coordination — all driven by a PostgreSQL-backed real-time core.' },
      { label: 'Booking Engine', text: 'A direct-booking layer that sits in front of the PMS, supporting rate plans, seasonal pricing, and occupancy-aware availability without going through a third party.' },
      { label: 'ERP Layer', text: 'Finance, procurement, payroll, and reporting modules tied to the same data model, so revenue actuals and cost-of-sales reconcile against bookings without manual exports.' },
      { label: 'Cross-platform Frontends', text: 'A Flutter client for front-desk and housekeeping staff, plus web admin panels for finance and management — all hitting the same Node.js API.' },
    ],
    heroQuote: 'One platform, every property.',
  },
  {
    id: 8,
    title: 'Multi-site Oil Production System',
    slug: 'oil-field-management',
    category: 'Enterprise Software',
    year: '2024',
    description: 'Multi-site operations management, real-time logistics tracking, and secure asset reporting for energy firms.',
    image: '/images/projects/oil-field-management/thumbnail.webp',
    technologies: ['Flutter', 'Node.js', 'PostgreSQL', 'Real-time Tracking', 'Enterprise Security'],
    layoutVariant: 'zubene',
    challengeTitle: 'The Platform',
    challengeContent: 'Energy firms running multiple production sites face a coordination problem: dispatchers, field engineers, transport crews, and head-office reporting all live in different tools, and most of those tools were built for a single site. The brief was a single operations layer covering live logistics, asset status, and audit-ready reporting across every site at once.',
    solutionIntro: 'We built a multi-site operations platform engineered for scale and security:',
    solutionPoints: [
      { label: 'Real-time Tracking', text: 'Vehicle and crew positions stream in continuously, with site-by-site dashboards showing live status against planned schedules.' },
      { label: 'Asset Reporting', text: 'Production figures, equipment status, and incident logs roll up from each site into a consolidated reporting layer with role-based visibility.' },
      { label: 'Hardened Auth & Audit', text: 'Every read and write is attributable, with enterprise-grade access control and an immutable audit trail suitable for regulatory review.' },
      { label: 'Field-ready Mobile', text: 'A Flutter client designed for offline-tolerant field use — engineers and drivers can record activity even when network coverage drops, with state syncing back when they reconnect.' },
    ],
    heroQuote: 'Every site, every shift, one operations layer.',
  },
  {
    id: 9,
    title: 'DC-RMS Restaurant Management',
    slug: 'dc-rms-restaurant-management',
    category: 'SaaS Platform',
    year: '2024',
    description: 'Multi-tenant restaurant management with real-time Kitchen, Bar, and Hookah order processing, role-based access, and offline support.',
    image: '/images/projects/dc-rms/thumbnail.webp',
    technologies: ['Flutter', 'Shorebird Code Push', 'Firebase Firestore', 'Hybrid Database'],
    layoutVariant: 'zubene',
    challengeTitle: 'The Platform',
    challengeContent: 'DC-RMS is a multi-tenant restaurant management system where every order placed at the table fans out instantly to the right station — Kitchen, Bar, or Hookah — without staff having to relay it manually. Designed for venues with multiple service stations and split workflows, it has to keep working even when the venue\'s internet does not.',
    solutionIntro: 'We built DC-RMS as a multi-tenant SaaS platform with offline-first foundations:',
    solutionPoints: [
      { label: 'Station-aware Order Routing', text: 'Orders are tagged at entry and routed to Kitchen, Bar, or Hookah displays in real time, so each station only sees what it owns.' },
      { label: 'Role-based Access', text: 'Servers, station leads, managers, and owners each get a tailored interface, with permissions enforced at the data layer rather than just the UI.' },
      { label: 'Hybrid Online/Offline DB', text: 'A local cache keeps the floor running through internet outages, with Firestore reconciling state when the connection returns.' },
      { label: 'Live App Updates', text: 'Shorebird code push lets the same app update across every venue without forcing operators through app-store review cycles.' },
    ],
    heroQuote: 'Every order, every station, in real time.',
  },
  {
    id: 10,
    title: 'Sub Soccer',
    slug: 'sub-soccer',
    category: 'Mobile App',
    year: '2024',
    description: 'Customer app with real-time integration to live games via QR codes — team creation, leaderboards, charts, and points tracking for casual sub-soccer leagues.',
    image: '/images/projects/sub-soccer/thumbnail.webp',
    technologies: ['React Native', 'RabbitMQ', 'PostgreSQL', 'Redis', 'IoT Sync Engine'],
    layoutVariant: 'zubene',
    challengeTitle: 'The App',
    challengeContent: 'Sub-soccer venues run dozens of casual matches every week — most of them never recorded anywhere. The brief was an app that lets a player scan a QR code at the pitch, join a live game, and see the score, leaderboard, and stats update in real time as the match plays out, without any manual scoring.',
    solutionIntro: 'We built a real-time sports platform connecting players, pitches, and leaderboards:',
    solutionPoints: [
      { label: 'QR-driven Game Join', text: 'A scan at the pitch drops a player straight into the live match — no account hunting, no manual lobby setup, no friction at the kickoff whistle.' },
      { label: 'Live Score Sync', text: 'Match events flow through a RabbitMQ event bus with Redis-backed read models, so the score on every player\'s phone stays seconds-fresh.' },
      { label: 'Team & League Tools', text: 'Players form teams, track ranking across seasons, and view per-game stats with charts that turn pickup matches into a measurable league.' },
      { label: 'IoT Sync Engine', text: 'A bridge layer reconciles signals from venue hardware against app state, so the leaderboard never desyncs from what actually happened on the pitch.' },
    ],
    heroQuote: 'Scan, play, climb the table.',
  },
  {
    id: 11,
    title: 'Dila Menu',
    slug: 'dila-menu',
    category: 'Web Platform',
    year: '2024',
    description: 'Contactless digital ordering system for restaurants and cafés — QR-driven menu browsing, order placement, and table service workflows.',
    image: '/images/projects/dila-menu/thumbnail.webp',
    technologies: ['Next.js 14', 'React 18', 'Firebase'],
    layoutVariant: 'zubene',
    challengeTitle: 'The Platform',
    challengeContent: 'Restaurants and cafés wanted to retire paper menus without forcing customers to install yet another app. Dila Menu is the layer between the QR code on the table and the kitchen — guests scan, browse, order, and pay, while staff get a live table-by-table queue without changing how the floor runs.',
    solutionIntro: 'We built Dila Menu as an install-free, table-aware ordering system:',
    solutionPoints: [
      { label: 'QR-to-Web Flow', text: 'A scan opens the menu instantly in the phone\'s browser — no app install, no signup friction, no per-table reconfiguration.' },
      { label: 'Live Menu Management', text: 'Restaurant owners edit items, prices, and availability through a CMS-style backend, with changes reflected on every active table immediately.' },
      { label: 'Table-aware Orders', text: 'Each scan carries the table identifier, so the kitchen knows exactly where every order is going without staff having to reconcile tickets manually.' },
      { label: 'Operator Dashboard', text: 'Live order queue, status updates, and historical analytics — built on Firebase so kitchen staff get push-fresh data without polling.' },
    ],
    heroQuote: 'Scan the table, skip the queue.',
  },
  {
    id: 12,
    title: 'Kurdish Calendar',
    slug: 'kurdish-calendar',
    category: 'Mobile App',
    year: '2024',
    description: 'The official KRG-aligned calendar app — public holiday tracking, Kurdish/Gregorian/Hijri date conversion, and event reminders for Kurdistan.',
    image: '/images/projects/kurdish-calendar/thumbnail.webp',
    technologies: ['Flutter', 'SQLite', 'KRG API Integration', 'Custom Date Algorithms'],
    layoutVariant: 'zubene',
    challengeTitle: 'The App',
    challengeContent: 'Three calendar systems sit side by side in everyday Kurdish life — the Kurdish solar calendar, the Gregorian calendar, and the Hijri lunar calendar — and most off-the-shelf calendar apps only handle one of them. Kurdish Calendar brings all three together, KRG public holidays included, in a single tool that works the same way the country actually does.',
    solutionIntro: 'We engineered a tri-calendar system grounded in custom date math:',
    solutionPoints: [
      { label: 'Three Calendars, One View', text: 'Every date is rendered simultaneously in Kurdish, Gregorian, and Hijri systems, with conversions handled by a custom algorithm rather than a generic library.' },
      { label: 'Official Holiday Tracking', text: 'KRG-aligned public holidays surface automatically, with reminders ahead of each occasion so users don\'t miss culturally significant dates.' },
      { label: 'Offline-first SQLite Core', text: 'Date data and event reminders are stored locally so the app works without connectivity, with KRG API sync running in the background when online.' },
      { label: 'Cross-platform Flutter Build', text: 'A single codebase ships to iOS and Android with native-quality typography for Kurdish, Arabic, and Latin scripts.' },
    ],
    heroQuote: 'Three calendars, one Kurdish view.',
  },
  {
    id: 13,
    title: 'SooqSolar',
    slug: 'sooqsolar',
    category: 'Marketplace Platform',
    year: '2024',
    description: 'Solar marketplace platform connecting customers with verified solar installation companies — energy calculators and instant quote generation.',
    image: '/images/projects/sooqsolar/thumbnail.webp',
    technologies: ['Flutter', 'React', 'Node.js', 'PostgreSQL', 'JWT Auth', 'Cloud Infrastructure'],
    layoutVariant: 'zubene',
    challengeTitle: 'The Platform',
    challengeContent: 'Buying a residential solar system in the region is opaque — homeowners don\'t know what they need, installers don\'t know what they can sell, and quotes vary wildly between vendors. SooqSolar is a marketplace that turns "I think I need solar" into a vetted shortlist of installers and a comparable set of quotes within minutes.',
    solutionIntro: 'We built SooqSolar as a two-sided marketplace anchored by an energy calculator:',
    solutionPoints: [
      { label: 'Energy Calculator', text: 'Homeowners enter their consumption profile and get a sized system spec — panel count, inverter range, expected output — before any installer is involved.' },
      { label: 'Verified Installer Network', text: 'Solar companies onboard through a vetting flow before they can quote, raising baseline quality on the supply side and protecting customers from unqualified vendors.' },
      { label: 'Instant Quote Generation', text: 'Customer specs feed installer dashboards, which return instant quotes that customers can compare apples-to-apples rather than chasing vendors one by one.' },
      { label: 'Multi-platform Reach', text: 'A Flutter client for homeowners and a React dashboard for installers — both backed by the same Node.js API and PostgreSQL data model.' },
    ],
    heroQuote: 'From "thinking about solar" to comparable quotes.',
  },
  {
    id: 14,
    title: 'Orvinex',
    slug: 'orvinex',
    category: 'E-commerce',
    year: '2024',
    description: 'A multi-platform shopping app with live order tracking, secure checkout, and international logistics built in.',
    image: '/images/projects/orvinex/thumbnail.webp',
    technologies: ['Flutter', 'BLoC', 'OneSignal', 'Google Maps', 'Dio', 'GetIt', 'SQLite', 'Firebase Config'],
    layoutVariant: 'zubene',
    challengeTitle: 'The App',
    challengeContent: 'Orvinex is a multi-platform e-commerce experience built for shoppers who expect every part of the order — from cart to doorstep — to feel coherent. The brief was a polished Flutter client capable of secure checkout, real-time delivery tracking, and locale-specific configuration without the typical performance compromises of cross-platform apps.',
    solutionIntro: 'We engineered Orvinex around BLoC architecture for predictable state at scale:',
    solutionPoints: [
      { label: 'BLoC State Management', text: 'Cart, checkout, and order tracking each run as isolated BLoCs with clearly defined inputs and outputs, keeping the UI deterministic even as the app surface grows.' },
      { label: 'Real-time Delivery Tracking', text: 'Google Maps integration shows live courier position from dispatch to handoff, with OneSignal push driving stage-by-stage notifications.' },
      { label: 'Resilient Networking Layer', text: 'Dio + GetIt run a clean dependency-injected HTTP layer that recovers from flaky connections, with SQLite caching keeping the catalogue fast offline and Firebase Remote Config flipping promotions and pricing without app-store releases.' },
      { label: 'Secure Checkout', text: 'A hardened payment flow validated against multiple regional gateways, with token-based session handling that survives backgrounding and device handoffs.' },
    ],
    heroQuote: 'Every step, from cart to doorstep.',
  },
  {
    id: 15,
    title: 'CR Medical',
    slug: 'cr-medical',
    category: 'Healthcare',
    year: '2024',
    description: 'Multi-language medical ecosystem app with patient dashboard, health tracker, doctor search, and nurse analysis reports.',
    image: '/images/projects/cr-medical/thumbnail.webp',
    technologies: ['Flutter', 'Riverpod', 'PostgreSQL', 'HIPAA-ready Auth', 'Secure S3'],
    layoutVariant: 'zubene',
    challengeTitle: 'The Platform',
    challengeContent: 'CR Medical is a healthcare ecosystem covering patients, doctors, and nurses in a single platform — daily health tracking on the patient side, clinical search and analysis tools on the practitioner side, all with the auditability and access discipline that medical data demands. Multi-language support is non-negotiable in a region where consultations cross Kurdish, Arabic, and English in the same room.',
    solutionIntro: 'We engineered CR Medical with patient privacy and clinician workflows held in equal weight:',
    solutionPoints: [
      { label: 'Patient Dashboard', text: 'Vitals tracking, medication reminders, appointment history, and longitudinal health trends, presented in the patient\'s preferred language.' },
      { label: 'Doctor Search & Booking', text: 'A directory layer that surfaces practitioners by specialty, language, and location, with in-app booking tied to clinician calendars.' },
      { label: 'Nurse Analysis Reports', text: 'Structured input flows for nurses to record observations and lab analyses, with reports surfacing back to the responsible clinician.' },
      { label: 'HIPAA-ready Foundations', text: 'Hardened auth, encrypted-at-rest storage on Secure S3, and PostgreSQL access policies designed for medical-grade audit trails.' },
    ],
    heroQuote: 'A medical ecosystem, in three languages.',
  },
  {
    id: 16,
    title: 'TAAKLINE',
    slug: 'taakline',
    category: 'Logistics Platform',
    year: '2024',
    description: 'Logistics and delivery service ecosystem connecting customers, drivers, and merchants — real-time GPS tracking and merchant dashboards.',
    image: '/images/projects/taakline/thumbnail.webp',
    technologies: ['Microservices', 'Redis', 'GPS Tracking Engine', 'Merchant Dashboards'],
    layoutVariant: 'zubene',
    challengeTitle: 'The Platform',
    challengeContent: 'TAAKLINE is a three-sided logistics platform — customers, drivers, and merchants — built around the principle that everyone should see the same parcel state at the same time. Real-time GPS, merchant fulfillment dashboards, and a customer-facing tracking experience all read from the same source of truth.',
    solutionIntro: 'We architected TAAKLINE around a microservices core with a real-time tracking engine:',
    solutionPoints: [
      { label: 'Three-sided Apps', text: 'Dedicated experiences for customers, drivers, and merchants, each tuned for their specific workflow — order placement, route navigation, fulfillment management — all hitting the same backend.' },
      { label: 'GPS Tracking Engine', text: 'Driver positions stream into a tracking service, with Redis-backed read models powering customer-facing maps without hammering the source service.' },
      { label: 'Merchant Dashboards', text: 'Merchants get a live operational view: order queue, in-transit parcels, delivery confirmations, and reconciliation reports.' },
      { label: 'Microservices Backbone', text: 'Order, dispatch, tracking, and merchant services scale independently, so peak hours in one part of the system don\'t cascade into others.' },
    ],
    heroQuote: 'One source of truth, three apps in sync.',
  },
];

export interface Service {
  slug: string;
  title: string;
  description: string;
  // Project technologies that imply this service was delivered — used to
  // surface related case studies on the service page. Match is case-insensitive
  // and partial (substring) so 'Flutter' matches 'Flutter / Dart'.
  relatedTechMatchers: string[];
}

export const services: Service[] = [
  {
    slug: 'mobile-app-development',
    title: 'Mobile App Development',
    description: 'You get a real app on the App Store and Google Play — one codebase, both platforms. We build it, ship it, and stay on for the updates after launch.',
    relatedTechMatchers: ['Flutter', 'React Native', 'iOS', 'Android', 'Swift', 'Kotlin'],
  },
  {
    slug: 'web-design-development',
    title: 'Web Design & Development',
    description: 'A website or web app that loads fast, works on every screen, and reads like your brand wrote it. We design and build it, then hand you the keys.',
    relatedTechMatchers: ['Next.js', 'React', 'Three.js', 'Sanity'],
  },
  {
    slug: 'ai-integration',
    title: 'AI Integration',
    description: 'AI that does a specific job inside your product — answering customers, sorting documents, pulling answers out of your own data. We scope it to one real task and wire it in.',
    relatedTechMatchers: ['LLM', 'AI', 'Python', 'AI/ML', 'Explainable AI', 'Rust'],
  },
  {
    slug: 'digital-strategy-consultation',
    title: 'Digital Strategy & Consultation',
    description: 'You sit down with the engineers who would actually build it. We tell you what to build first, what to skip, and which tech choices you will regret in a year.',
    relatedTechMatchers: ['Microservices', 'Enterprise Security'],
  },
  {
    slug: 'ui-ux-design',
    title: 'UI/UX Design',
    description: 'Screens people understand without a manual. We map the flows, design the interface, and hand off files your developers can build from — or we build them ourselves.',
    relatedTechMatchers: ['UI', 'UX'],
  },
  {
    slug: 'e-commerce-solutions',
    title: 'E-Commerce Solutions',
    description: 'An online store that takes payment, tracks stock, and shows you what is selling. Built to handle local payment methods, not just the ones that work in Silicon Valley.',
    relatedTechMatchers: ['Payment Gateway', 'MongoDB', 'Node.js'],
  },
  {
    slug: 'humanitarian-charity-app-development',
    title: 'Humanitarian and Charity App Development',
    description: 'Apps for NGOs and charities that work on a tight budget and in places with bad connectivity. We have built for this region, so we know what breaks in the field.',
    relatedTechMatchers: [],
  },
  {
    slug: 'social-media-management',
    title: 'Social Media Management',
    description: 'We run your social accounts — plan the posts, write them, publish them — so the feed stays alive without you watching it every day.',
    relatedTechMatchers: [],
  },
  {
    slug: 'content-creation',
    title: 'Content Creation',
    description: 'Words, images, and video that sound like you and say something. We make the content; you stop staring at a blank page.',
    relatedTechMatchers: [],
  },
  {
    slug: 'data-analytics',
    title: 'Data Analytics',
    description: 'A dashboard that answers the questions you actually ask — what sold, what stalled, what to do next — pulled live from your own systems.',
    relatedTechMatchers: ['Real-time Tracking', 'IoT', 'PostgreSQL'],
  },
];

export const skills = [
  'Web Design',
  'Mobile App Development',
  'UI/UX Design',
  'AI agents and integrations',
  'TECH DUE DILIGENCE',
  'FRACTIONAL CTO AND TEAM BUILDING',
  'MVP MOBILE/WEBSITE',
];

export const contactInfo = {
  email: 'info@datacode.app',
  emailSecondary: 'info@datacodeapp.com',
  phone: '+964-751-449-1008',
  address: 'B80, Floor 20, MRF 2 towers, English village Rd, Erbil, Kurdistan Region, Iraq',
  coordinates: { lat: 36.1911, lng: 44.0094 },
  socialLinks: [
    { name: 'Instagram', href: 'https://instagram.com/datacodeapp', icon: 'Ig' },
    { name: 'Twitter', href: 'https://twitter.com/datacodeapp', icon: 'Tw' },
    { name: 'Facebook', href: 'https://facebook.com/datacodeapp', icon: 'Fb' },
    { name: 'LinkedIn', href: 'https://linkedin.com/company/datacodeapp', icon: 'Li' },
    { name: 'YouTube', href: 'https://youtube.com/@datacodeapp', icon: 'Yt' },
  ],
};
